import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../Components/apiService'; // Import ApiService class
import { BASE_URL, DateFormat } from '../Components/Constant';
import {
  Layout,
  Card,
  Typography,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  Space,
  Avatar,
  Row,
  Col,
  Descriptions,
  Tooltip,
  message, // Remove static message import if not needed elsewhere
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  CloudOutlined,
  TableOutlined,
  SettingOutlined,
  ContainerOutlined,
} from '@ant-design/icons';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const ObjectsDashboard = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [objectFields, setObjectFields] = useState([]);
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState('loading');

  // Use the message hook to get a context-aware message API
  const [messageApi, contextHolder] = message.useMessage();

  const fetchObjects = async () => {
    if (!orgId) {
      console.error('No orgId found in URL params.');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log(`Fetching objects for organization ID: ${orgId}`);
      const apiServiceObjectData = new ApiService(
                `${BASE_URL}/super_admin/organizations//${orgId}/get_object_details`,
                { 'Content-Type': 'application/json' },
                'GET'
              );
          const apiResponse = await apiServiceObjectData.makeCall();
      
      if (apiResponse && apiResponse.length > 0) {
        console.log('Using API data for objects');
        const mappedObjects = apiResponse.map(obj => ({
          key: obj.object_details._id,
          id: obj.object_details._id,
          name: obj.object_details.label,
          apiName: obj.object_details.name,
          type: obj.object_details.is_system ? 'Standard' : 'Custom',
          recordCount: obj.record_count,
          lastModified: obj.object_details.updated_at,
          fieldDetails: obj.field_details,
        }));
        setObjects(mappedObjects);
        setDataSource('api');
      } else {
        console.log('No API data, using fallback');
        setObjects([]);
        setDataSource('fallback');
      }
    } catch (error) {
      console.error('❌ Error fetching objects from API:', error);
      setObjects([]);
      setDataSource('fallback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObjects();
  }, [orgId]);

  const handleAddObject = (values) => {
    const newObject = {
      key: Date.now(),
      name: values.name,
      apiName: values.apiName,
      type: 'Custom',
      recordCount: 0,
      lastModified: new Date().toISOString(),
    };
    setObjects([...objects, newObject]);
    setIsAddModalVisible(false);
    form.resetFields();
    messageApi.success('Object added successfully!'); // Changed from message.success
  };

  const handleDeleteObject = (objectKey) => {
    setObjects(objects.filter(obj => obj.key !== objectKey));
    messageApi.success('Object deleted successfully!'); // Changed from message.success
  };

  const handleViewObjectFields = async (record) => {
    setSelectedObject(record);
    setLoading(true);
    try {
      if (record.fieldDetails) {
        const mappedFields = record.fieldDetails.map(field => ({
          key: field._id,
          fieldName: field.label,
          apiName: field.name,
          type: field.type,
          isCustom: !field.is_system,
          lastModified: field.updated_at,
          createdDate: field.created_at,
        }));
        setObjectFields(mappedFields);
        messageApi.success(`Loaded fields for ${record.name}`); // Changed from message.success
      } else {
        setObjectFields([]);
        messageApi.warning('No field details found for this object.'); // Changed from message.warning
      }
    } catch (error) {
      messageApi.error(`Failed to fetch fields for ${record.name}.`); // Changed from message.error
      console.error('Error fetching object fields:', error);
      setObjectFields([]);
    } finally {
      setLoading(false);
      setIsViewModalVisible(true);
    }
  };

  const columns = [
    {
      title: 'Object Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <Avatar size="small" icon={<TableOutlined />} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'API Name',
      dataIndex: 'apiName',
      key: 'apiName',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color={type === 'Custom' ? 'blue' : 'green'}>{type}</Tag>,
    },
    {
      title: 'Record Count',
      dataIndex: 'recordCount',
      key: 'recordCount',
      sorter: (a, b) => a.recordCount - b.recordCount,
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Fields">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewObjectFields(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this object?"
            onConfirm={() => handleDeleteObject(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Object">
              <Button danger icon={<DeleteOutlined />} type="link" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const fieldColumns = [
    {
      title: 'Field Name',
      dataIndex: 'fieldName',
      key: 'fieldName',
      render: (text) => (
        <Space>
          <ContainerOutlined style={{ color: '#1890ff' }} />
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'API Name',
      dataIndex: 'apiName',
      key: 'apiName',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Custom',
      dataIndex: 'isCustom',
      key: 'isCustom',
      render: (isCustom) => <Tag color={isCustom ? 'blue' : 'green'}>{isCustom ? 'Yes' : 'No'}</Tag>,
    },
    {
      title: 'Last Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {contextHolder} {/* Add this line */}
      <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(-1)}
                style={{ marginRight: 16 }}
              >
                Back
              </Button>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                <CloudOutlined style={{ marginRight: 8 }} />
                Object Manager
              </Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsAddModalVisible(true)}
              >
                Create New Object
              </Button>
            </Space>
          </Col>
        </Row>
      </Header>

      <Content style={{ margin: '24px' }}>
        <Card
          title="Custom & Standard Objects"
          extra={<Text type="secondary">{objects.length} objects</Text>}
        >
          <Table
            columns={columns}
            dataSource={objects}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </Content>

      {/* Add Object Modal */}
      <Modal
        title="Create New Custom Object"
        open={isAddModalVisible}
        zIndex={1002}   // higher than sidebar
        onCancel={() => {
          setIsAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAddObject}>
          <Form.Item
            name="name"
            label="Object Name"
            rules={[{ required: true, message: 'Please enter organization name' }]}
          >
            <Input placeholder="e.g., Project" />
          </Form.Item>
          <Form.Item
            name="apiName"
            label="API Name"
            rules={[
              { required: true, message: 'Please enter API name' },
              { pattern: /^[a-zA-Z_]+__c$/, message: 'Must end with __c and contain only letters and underscores' },
            ]}
          >
            <Input placeholder="e.g., Project__c" />
          </Form.Item>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setIsAddModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Create Object
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Object Fields Modal (New) */}
      <Modal
        title={`Fields for ${selectedObject?.name}`}
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        zIndex={1002}   // higher than sidebar
        footer={null}
        width={800}
      >
        <Card size="small" style={{ marginBottom: 16 }}>
            <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="API Name">{selectedObject?.apiName}</Descriptions.Item>
                <Descriptions.Item label="Type">
                    <Tag color={selectedObject?.type === 'Custom' ? 'blue' : 'green'}>{selectedObject?.type}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Record Count">{selectedObject?.recordCount}</Descriptions.Item>
                <Descriptions.Item label="Last Modified">{selectedObject?.lastModified && new Date(selectedObject.lastModified).toLocaleDateString()}</Descriptions.Item>
            </Descriptions>
        </Card>
        
        <Table
          columns={fieldColumns}
          dataSource={objectFields}
          loading={loading}
          rowKey="key"
          pagination={false}
        />
      </Modal>
    </Layout>
  );
};

export default ObjectsDashboard;